import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import { Divider } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { format } from "date-fns";
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { PauseEndPlayIcon } from "./icons/pauseEndPlay";
import { WaitIcon } from "./icons/wait";
import { getTasks } from "../api/tasks";

interface AccordionProps {
  title: string;
  subtitle: string;
  createdAt: string;
  requestId: string;
  defaultExpanded: boolean;
  tenantName: string;
}

interface TasksListType {
  data: TasksType;
  requestId: string;
}

function TasksList({
  data: { id, taskKey, taskName, status, isSignatureTask },
  requestId,
}: TasksListType): JSX.Element {
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    if (id) {
      const item = localStorage.getItem(id);
      item && setIsSaved(true);
    }
  }, [id]);

  const verifySaved = (): JSX.Element => {
    if (isSaved) {
      return <PauseEndPlayIcon sx={{ color: "#737373", fontSize: "1.8rem" }} />;
    }

    if (status === "NOT_EXECUTABLE") {
      return <WaitIcon sx={{ color: "#cfcfcf", fontSize: "1.8rem" }} />;
    }

    return (
      <PlayCircleOutlinedIcon
        sx={{ color: status === "ACTIVE" ? "#737373" : "#cfcfcf" }}
        width="90px"
      />
    );
  };

  return (
    <Box
      key={taskKey}
      onClick={() => {
        if (status !== "ACTIVE" || !id) return;

        let pathTask = "task";
        if (isSignatureTask) {
          pathTask = "signature";
        }
        void navigate({ to: `/${pathTask}/${requestId}/${id}` as any });
      }}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        cursor: status === "ACTIVE" ? "pointer" : "default",
        justifyContent: "space-between",
        marginBottom: "8px",
      }}
    >
      <Typography fontSize={12} fontWeight={400}>
        {taskName}
      </Typography>
      {status === "COMPLETED" ? (
        <CheckCircleOutlineOutlinedIcon sx={{ color: "green" }} width="90px" />
      ) : (
        verifySaved()
      )}
    </Box>
  );
}

function CustomAccordion({
  title,
  subtitle,
  createdAt,
  requestId,
  defaultExpanded,
  tenantName,
}: AccordionProps): React.JSX.Element {
  const targetRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const [tasks, setTasks] = useState<TasksType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [messageError, setMessageError] = useState<string | undefined>(
    undefined,
  );

  const fetchTasks = async (): Promise<void> => {
    setIsLoading(true);
    targetRef.current &&
      targetRef.current.scrollIntoView({ behavior: "instant" });
    try {
      const returnTasks = await getTasks(requestId);
      setTasks(returnTasks);
    } catch (err) {
      setMessageError(
        t("toast.unexpectedError"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (defaultExpanded) {
      void fetchTasks();
    }
  }, [defaultExpanded]);

  return (
    <>
      <Accordion
        defaultExpanded={defaultExpanded}
        ref={targetRef}
        sx={{
          backgroundColor: "#fafafa",
          boxShadow: "none",
          position: "inherit",
        }}
      >
        <AccordionSummary
          aria-controls="panel2-content"
          expandIcon={<ArrowDropDownIcon />}
          id="panel2-header"
          onClick={() => void fetchTasks()}
          sx={{ paddingRight: 0, paddingLeft: 0 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box>
              <Link
                color="text.primary"
                style={{ textDecorationColor: theme.palette.text.primary }}
                to={`/request-history/$requestId`}
                params={{
                  requestId,
                }}
                state={
                  {
                    title: title.split(" - ")[1]
                      ? title.split(" - ")[1]
                      : title,
                    nup: title.split(" - ")[0] || "",
                    tenantName,
                  } as any
                }
              >
                <Typography
                  color="text.primary"
                  fontSize={14}
                  fontWeight={500}
                  variant="subtitle2"
                >
                  {title}
                </Typography>
              </Link>
              <Typography color="text.secondary" fontSize={12} fontWeight={400}>
                {format(new Date(createdAt), "dd/MM/yyyy")}
              </Typography>
            </Box>

            <Box
              sx={{
                textAlign: "right",
                marginLeft: 2,
              }}
            >
              <Typography color="text.secondary" fontSize={12} fontWeight={400}>
                {subtitle}
              </Typography>
              <Typography color="text.secondary" fontSize={12} fontWeight={400}>
                {tenantName}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <AccordionDetails sx={{ paddingRight: 0, paddingLeft: 0 }}>
            {tasks.length > 0 ? (
              tasks.map((task) => (
                <TasksList
                  data={task}
                  key={task.taskKey}
                  requestId={requestId}
                />
              ))
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography fontSize={14} fontWeight={400}>
                  {messageError ? messageError : t("home.noTaskFound")}
                </Typography>
                {messageError ? (
                  <ReplayOutlinedIcon
                    onClick={() => void fetchTasks()}
                    sx={{ color: "#737373" }}
                  />
                ) : null}
              </Box>
            )}
          </AccordionDetails>
        )}
      </Accordion>
      <Divider />
    </>
  );
}

export default CustomAccordion;
